import PropTypes from "prop-types"
import React, { useState } from "react"
import { Form, FormFeedback, Label } from "reactstrap"

import LoadingButton from "../../components/Common/LoadingButton"
import AuthLayout from "../../components/Common/AuthLayout"
import PasswordInput from "../../components/Common/PasswordInput"
import PasswordRequirementsHint from "../../components/OnboardingComponents/PasswordRequirementsHint"

import { Link } from "react-router-dom"
import withRouter from "../../components/Common/withRouter"

import { toast, Bounce } from "react-toastify"
import { post } from "../../api/axios"
import { toastApiError } from "../../helpers/apiError"
import endpoints from "../../api/endpoints"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
} from "../../helpers/passwordRules"

const ResetPassword = props => {
  document.title = " Reset Password | AMOK . EARTH"

  const navigate = props.router?.navigate
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      password: "",
      password_confirmation: "",
      logout_all_devices: false,
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Please enter your password")
        .min(8, "The password field must be at least 8 characters.")
        .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async values => {
      const token = localStorage.getItem("auth_token")
      if (isSubmitting) return
      setIsSubmitting(true)
      try {
        const response = await post(
          endpoints.resetPassword,
          {
            password: values.password,
            password_confirmation: values.password_confirmation,
            logout_all_devices: false,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (response?.data?.success) {
          localStorage.removeItem("auth_token")
          localStorage.removeItem("user_data")
          localStorage.removeItem("reset_email")
          localStorage.removeItem("otp_type")
          localStorage.removeItem("fcm")

          toast.success(
            "Password reset successfully. Please login with your new password.",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
          navigate("/login")
        } else {
          toastApiError(response, "Password reset failed. Please try again.")
        }
      } catch (error) {
        toastApiError(error, "Password reset failed. Please try again.")
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  return (
    <AuthLayout
      title="Reset Password"
      subtitle="Set your new password below."
    >
      <Form
        className="auth-page__form"
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <div className="auth-page__field">
          <Label className="auth-page__label">Password</Label>
          <PasswordInput
            name="password"
            value={validation.values.password || ""}
            placeholder="Enter Password"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            invalid={
              !!getPasswordFieldError(
                validation.touched.password,
                validation.errors.password,
              )
            }
          />
          <div className="mt-2">
            <PasswordRequirementsHint value={validation.values.password} />
          </div>
          {getPasswordFieldError(
            validation.touched.password,
            validation.errors.password,
          ) ? (
            <FormFeedback type="invalid" className="d-block">
              {getPasswordFieldError(
                validation.touched.password,
                validation.errors.password,
              )}
            </FormFeedback>
          ) : null}
        </div>

        <div className="auth-page__field">
          <Label className="auth-page__label">Confirm Password</Label>
          <PasswordInput
            name="password_confirmation"
            value={validation.values.password_confirmation || ""}
            placeholder="Enter Password Confirmation"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            invalid={
              !!(
                validation.touched.password_confirmation &&
                validation.errors.password_confirmation
              )
            }
          />
          {validation.touched.password_confirmation &&
          validation.errors.password_confirmation ? (
            <FormFeedback type="invalid">
              {validation.errors.password_confirmation}
            </FormFeedback>
          ) : null}
        </div>

        <LoadingButton loading={isSubmitting}>Submit</LoadingButton>

        <p className="auth-page__divider">Or</p>

        <p className="auth-page__footer-text">
          Remember your password? <Link to="/login">Login</Link>
        </p>
      </Form>
    </AuthLayout>
  )
}

export default withRouter(ResetPassword)

ResetPassword.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
  loginUser: PropTypes.func,
  socialLogin: PropTypes.func,
}
